/**
 * Targeting Rule Validation
 *
 * Client-side validation helpers that call backend validation API
 */

import type { TargetingRuleV1, ValidationResult, Condition } from "./types";
import { validateTargetingRule as apiValidateTargetingRule } from "../../api/campaigns";

/** True when the rule tree contains at least one leaf condition (not only empty groups). */
function conditionHasLeafConditions(condition: Condition): boolean {
  if (condition.type === "and" || condition.type === "or") {
    if (!condition.conditions?.length) return false;
    return condition.conditions.some((c) => conditionHasLeafConditions(c));
  }
  if (condition.type === "not") {
    const inner = condition.conditions?.[0];
    return inner ? conditionHasLeafConditions(inner) : false;
  }
  return true;
}

/** Whether the campaign has real targeting configured (merchant-facing “active” state). */
export function hasActionableTargetingRules(targetingRules: unknown): boolean {
  if (targetingRules == null || typeof targetingRules !== "object") return false;
  const t = targetingRules as Partial<TargetingRuleV1>;
  if (t.version !== "1" || t.rule == null) return false;
  return conditionHasLeafConditions(t.rule as Condition);
}

const FRIENDLY_VALIDATION_MESSAGES: Record<string, string> = {
  "Logical condition must have at least one subcondition":
    "This group is empty. Add a condition or remove the group.",
};

function mapValidationMessages(errors: ValidationResult["errors"]): ValidationResult["errors"] {
  return errors.map((e) => ({
    ...e,
    message: FRIENDLY_VALIDATION_MESSAGES[e.message] ?? e.message,
  }));
}

/**
 * Validate targeting rule via backend API
 */
export async function validateTargetingRule(
  rule: TargetingRuleV1
): Promise<ValidationResult> {
  try {
    const result = await apiValidateTargetingRule(rule);
    if (!result.valid && result.errors?.length) {
      return {
        ...result,
        errors: mapValidationMessages(result.errors),
      };
    }
    return result;
  } catch (error) {
    console.error("Validation error:", error);
    return {
      valid: false,
      errors: [
        {
          path: "rule",
          message: error instanceof Error ? error.message : "Validation failed",
          code: "VALIDATION_ERROR",
        },
      ],
    };
  }
}
